import type { Controls } from '../core/device';
import { clamp } from '../core/math';
import type { Random } from '../core/Random';
import { banner, roundRect } from './draw';
import type { MiniGame, MiniInput } from './types';

/** Where the arms are: -1 your knuckles hit the table, +1 theirs do. */
export interface ArmSource {
  /** Their push this frame (0..~1.5). */
  force(t: number, dt: number): number;
}

/** A regular at the Rusty Spur: pushes steady, then surges. */
export class BarOpponent implements ArmSource {
  private surge = 0;
  private surgeIn: number;
  constructor(
    private readonly rng: Random,
    readonly strength: number,
  ) {
    this.surgeIn = rng.range(1.2, 2.4);
  }
  force(t: number, dt: number): number {
    this.surgeIn -= dt;
    if (this.surgeIn <= 0) {
      this.surgeIn = this.rng.range(1.4, 2.8);
      this.surge = 0.6;
    }
    this.surge = Math.max(0, this.surge - dt);
    return this.strength * (0.85 + 0.15 * Math.sin(t * 3) + (this.surge > 0 ? 0.55 : 0));
  }
}

/**
 * Arm wrestling: mash the button to push. Every press adds a shove; keep a rhythm
 * going and you push harder. Whoever pins the other's knuckles to the table wins.
 */
export class ArmWrestleGame implements MiniGame {
  readonly title: string;
  readonly width = 360;
  readonly height = 240;
  /** -1 you lose, +1 you win. */
  arm = 0;
  private t = 0;
  private overT = -1;
  private you = 0;
  private lastPress = -1;
  private rhythm = 0;
  private presses = 0;
  /** If set, someone else decides where the arms are (the other player in a multiplayer match). */
  remoteArm: number | null = null;

  constructor(
    readonly opponent: string,
    private readonly source: ArmSource | null,
    /** Your strength: a little better with clout, a little worse drunk. */
    private readonly power = 1,
  ) {
    this.title = `Arm Wrestling vs. ${opponent}`;
  }

  get over(): boolean {
    return this.overT >= 0;
  }

  get done(): boolean {
    return this.over && this.t - this.overT > 1.4;
  }

  get won(): boolean {
    return this.over && this.arm > 0;
  }

  /** Your current push, for sending to the other player. */
  get push(): number {
    return this.you;
  }

  /** Presses this match. */
  get pressCount(): number {
    return this.presses;
  }

  hint(controls: Controls): string {
    const mash = { keys: 'Mash Space/E', touch: 'Tap', pad: 'Mash A' }[controls];
    return `${mash} as fast as you can. Keep a steady rhythm for extra muscle. Pin 'em!`;
  }

  /** Ends the match from outside (the other player's game decided it). */
  finish(arm: number): void {
    if (this.over) return;
    this.arm = arm;
    this.overT = this.t;
  }

  update(dt: number, input: MiniInput): void {
    this.t += dt;
    if (this.over) return;
    if (input.pressed || input.tap) {
      this.presses++;
      const gap = this.lastPress < 0 ? 1 : this.t - this.lastPress;
      // A steady 6-9 presses a second is the sweet spot.
      this.rhythm = clamp(this.rhythm + (gap > 0.1 && gap < 0.18 ? 0.25 : -0.15), 0, 1);
      this.lastPress = this.t;
      this.you += 0.36 * this.power * (1 + this.rhythm * 0.6);
    }
    this.you *= Math.exp(-dt * 2.5);
    if (this.remoteArm !== null) {
      this.arm = this.remoteArm;
    } else if (this.source) {
      const them = this.source.force(this.t, dt);
      this.arm += (this.you - them) * dt * 0.55;
    }
    this.arm = clamp(this.arm, -1, 1);
    if (Math.abs(this.arm) >= 1) this.overT = this.t;
  }

  draw(g: CanvasRenderingContext2D, time: number): void {
    const W = this.width;
    const H = this.height;
    g.fillStyle = '#2a1a12';
    g.fillRect(0, 0, W, H);
    // Neon in the back.
    g.fillStyle = 'rgba(255,79,163,0.35)';
    g.font = '30px Bangers, Impact, sans-serif';
    g.textAlign = 'center';
    g.fillText('COLD BEER', W / 2, 40);
    // The table.
    g.fillStyle = '#6b4a2b';
    roundRect(g, 40, 160, 280, 26, 6);
    g.fill();
    // Two arms meeting at the elbow pads, tilting with the match.
    const a = this.arm * 1.15;
    const cx = W / 2;
    const cy = 156;
    g.save();
    g.translate(cx, cy);
    g.rotate(a);
    g.fillStyle = '#eab08c';
    g.fillRect(-60, -16, 60, 16);
    g.fillStyle = '#c98c6c';
    g.fillRect(0, -16, 60, 16);
    g.fillStyle = '#d9a07c';
    g.beginPath();
    g.arc(0, -18, 14, 0, Math.PI * 2);
    g.fill();
    g.restore();
    // Strain shakes.
    const shake = this.over ? 0 : Math.sin(time * 50) * 1.5;
    g.fillStyle = '#ffd400';
    g.font = '14px Anton, Impact, sans-serif';
    g.fillText('YOU', 70 + shake, 210);
    g.fillStyle = '#5ff3ff';
    g.fillText(this.opponent.toUpperCase(), 290 - shake, 210);
    // Position meter.
    g.fillStyle = 'rgba(0,0,0,0.5)';
    roundRect(g, 60, 70, 240, 16, 8);
    g.fill();
    const mx = cx + this.arm * 116;
    g.fillStyle = this.arm > 0 ? '#9dff6a' : '#ff8a8a';
    g.fillRect(mx - 4, 66, 8, 24);
    // Rhythm bar.
    g.fillStyle = '#ff9a3c';
    g.fillRect(60, 96, 240 * this.rhythm, 6);
    if (this.over)
      banner(g, this.won ? 'PINNED ’EM!' : 'YOU GOT PINNED', cx, 130, this.won ? '#9dff6a' : '#ff8a8a', 38);
  }
}
